import { Field, Form, Formik } from "formik";
import { capitalize } from "lodash";
import {
  ChangeEvent,
  MouseEventHandler,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  CaseAdmin,
  CaseDocument,
  CaseDocumentAdmin as OpenApiDocument,
} from "pwapi";

import { CaseDocumentAdmin } from "custom-models";

import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";
import { selectFeatureFlag } from "shared/reducers/sysReducer";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import CustomInput from "components/customInputs/CustomInput";
import SelectInput from "components/customInputs/SelectInput";
import TextInput from "components/customInputs/TextInput";
import { useAppSelector } from "store/hooks";

import {
  autoAttachDocuments,
  canAttachDocument,
  historicalAssignableCategories,
  inputDisabledCategories,
  unassignableCategories,
} from "./editDocumentsFormUtils";
import { AdminFieldCase } from "../edit/fields";
import { CaseOption } from "../edit/ResourceOptions";

type DocumentsFormProps = {
  document: CaseDocumentAdmin;
  adminCase?: CaseAdminDetail;
  onSubmit: Function;
  onDelete?: Function;
  onClose: MouseEventHandler<HTMLButtonElement>;
  isUploading?: boolean;
  hasFlatCase?: boolean;
};

const DocumentsForm = ({
  document,
  onSubmit,
  onDelete,
  onClose,
  isUploading,
  hasFlatCase,
}: DocumentsFormProps) => {
  const useMergeCaseReportWorkflow = useAppSelector((state) =>
    selectFeatureFlag(state, "useMergeCaseReportWorkflow")
  );
  const getFileExtension = (filename: string): string | undefined => {
    const regexMatch = filename.match(/\.[^/.]+$/);
    return regexMatch?.length ? regexMatch[0] : undefined;
  };

  const fileExtension = useRef<string | undefined>(
    document.name ? getFileExtension(document.name) : undefined
  );

  const [disabled, setDisabled] = useState(false);

  const disabledCategories =
    document.case?.type === CaseAdmin.type.HISTORICAL
      ? inputDisabledCategories
      : [...inputDisabledCategories, ...historicalAssignableCategories];

  const documentCategories = useMemo(() => {
    const unassignCategories =
      document.case?.type === CaseAdmin.type.HISTORICAL
        ? unassignableCategories
        : [...unassignableCategories, ...historicalAssignableCategories];

    const oldUnassignableCategory = unassignCategories.includes(
      document.category
    )
      ? [
          {
            label: document.category.replaceAll("-", " "),
            value: document.category,
            disabled: true,
          },
        ]
      : [];
    return Object.values(OpenApiDocument.category)
      .filter((category) => {
        if (unassignCategories.includes(category)) return false;
        return true;
      })
      .map((p) => ({
        label: p.replaceAll("-", " "),
        value: p,
      }))
      .concat(oldUnassignableCategory)
      .sort((a, b) => {
        if (a.label === "Other") return 1;
        if (b.label === "Other") return -1;
        return a.label.localeCompare(b.label);
      });
  }, [
    document.category,
    document.case?.type,
    unassignableCategories,
    historicalAssignableCategories,
  ]);

  return (
    <Formik
      initialValues={document}
      enableReinitialize
      onSubmit={(values) => {
        if (disabled) return;

        setDisabled(true);
        onSubmit(values);
      }}
      validate={(values: any) => {
        // New document upload
        if (!values.id && (!values.category || !values.name)) {
          setDisabled(true);

          return;
        }

        // Existing document update
        if (values.id && !values.category) {
          setDisabled(true);

          return;
        }

        setDisabled(false);
      }}
    >
      {({ resetForm, values, dirty }) => (
        <Form className="AdminPanelForm">
          <div className="panelContainer">
            {isUploading && (
              <fieldset className="panelContainer__section">
                <legend className="panelContainer__section--title">
                  Upload
                </legend>
                <Field name="file">
                  {({ field, form }: any) => {
                    return (
                      <input
                        type="file"
                        accept="application/pdf"
                        data-testid="uploadDocumentFile"
                        {...field}
                        value={field.value ? undefined : ""}
                        onChange={(newFile: any) => {
                          form.setValues({
                            ...form.values,
                            file: newFile.target.files[0],
                            ...(!document.name && {
                              name: newFile.target.files[0].name,
                            }),
                          });
                        }}
                      />
                    );
                  }}
                </Field>
              </fieldset>
            )}
            <fieldset className="panelContainer__section">
              <legend className="panelContainer__section--title">
                Case Details
              </legend>
              {hasFlatCase && document.case ? (
                <CaseOption object={document.case} />
              ) : (
                <Field name="case">
                  {({ field, form }: any) => (
                    <AdminFieldCase
                      {...field}
                      disabled={
                        document.case?.status === CaseAdmin.status.LIS_PENDING
                      }
                      onChange={(newValue: any) => {
                        form.setValues({
                          ...form.values,
                          case: newValue.object,
                        });
                      }}
                    />
                  )}
                </Field>
              )}
            </fieldset>
            <fieldset className="panelContainer__section">
              <legend className="panelContainer__section--title">
                Documents Details
              </legend>

              <div className="file__container">
                <Field name="name">
                  {({ field }: any) => (
                    <TextInput
                      label="Name"
                      id="name"
                      {...field}
                      disabled={
                        document.case?.status === CaseAdmin.status.LIS_PENDING
                      }
                    />
                  )}
                </Field>
              </div>
              <div className="panelContainer__inputGroup">
                <div className="file__formatContainer">
                  <label>Status</label>
                  <span>{capitalize(document.status)}</span>
                </div>
                <Field name="category">
                  {({ field, form }: any) => (
                    <SelectInput
                      label="Category"
                      id="category"
                      options={documentCategories}
                      {...field}
                      disabled={
                        document.case?.status ===
                          CaseAdmin.status.LIS_PENDING ||
                        disabledCategories.includes(document.category)
                      }
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                        form.setValues({
                          ...form.values,
                          category: e.target.value,
                          attach_to_case_report:
                            useMergeCaseReportWorkflow &&
                            canAttachDocument(
                              e.target.value as CaseDocument.category
                            )
                              ? !!autoAttachDocuments.includes(
                                  e.target.value as CaseDocument.category
                                )
                              : undefined,
                        });
                      }}
                    />
                  )}
                </Field>
                {canAttachDocument(values.category) &&
                  useMergeCaseReportWorkflow && (
                    <Field name="attach_to_case_report">
                      {({ field, form }: any) => (
                        <CustomInput label="Append to Case Reports">
                          <div className="panelContainer__inputGroup">
                            <span className="panelContainer__checkbox">
                              <input
                                type="checkbox"
                                id="attach_to_case_report"
                                data-testid="attachCaseReport"
                                {...field}
                                value={!!field.value}
                                checked={field.value}
                                disabled={
                                  form.values.case?.status ===
                                  CaseAdmin.status.LIS_PENDING
                                }
                                onChange={() => {
                                  form.setFieldValue(
                                    "attach_to_case_report",
                                    !field.value
                                  );
                                }}
                              />
                              <label htmlFor="attach_to_case_report">
                                Append
                              </label>
                            </span>
                          </div>
                        </CustomInput>
                      )}
                    </Field>
                  )}
              </div>
            </fieldset>
          </div>
          <footer>
            <div className="panelContainer__inputGroup">
              <PathologyWatchButton onClick={onClose}>
                Cancel
              </PathologyWatchButton>
              {onDelete &&
                document &&
                document.category !== OpenApiDocument.category.CASE_REPORT && (
                  <PathologyWatchButton
                    color="red"
                    onClick={() => {
                      onDelete(document);
                    }}
                  >
                    Delete
                  </PathologyWatchButton>
                )}
            </div>
            <div className="panelContainer__inputGroup">
              <PathologyWatchButton
                onClick={() => {
                  resetForm();
                  if (isUploading) {
                    fileExtension.current = undefined;
                  }
                }}
                disabled={!dirty || disabled}
              >
                Discard Changes
              </PathologyWatchButton>
              <PathologyWatchButton
                type="submit"
                color="green"
                disabled={!dirty || disabled}
              >
                Save
              </PathologyWatchButton>
            </div>
          </footer>
        </Form>
      )}
    </Formik>
  );
};

export default DocumentsForm;
